import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client/react";
import { grey } from "@mui/material/colors";
import { map } from "lodash";
import AttachmentsList, { AttachmentListItem } from "@/components/AttachmentsList";
import { DispatchAttachmentsDocument, GetProjectDocument, SetDocumentAssociationTypeDocument, AssociationTypeEnum } from "@/graphql/generated/graphql";
import useDirectUpload from "@/hooks/useDirectUpload";
import { displayNotification } from "@/utils/notifications";

interface ProjectFilesProps {
  projectId: string | undefined;
}

const ProjectFilesDrawer: React.FC<ProjectFilesProps> = ({ projectId }) => {
  const [uploadingCount, setUploadingCount] = useState(0);
  const { uploadFile } = useDirectUpload();

  const [dispatchAttachments] = useMutation(DispatchAttachmentsDocument, {
    refetchQueries: [
      { query: GetProjectDocument, variables: { projectId: projectId ?? "" } },
    ],
  });

  const [setDocumentAssociationType] = useMutation(SetDocumentAssociationTypeDocument, {
    refetchQueries: [
      { query: GetProjectDocument, variables: { projectId: projectId ?? "" } },
    ],
  });

  const { data, loading } = useQuery(GetProjectDocument, {
    variables: { projectId: projectId ?? "" },
    skip: !projectId,
  });

  const onUploadFiles = async (files: File[]) => {
    if (!projectId || files.length === 0) return;
    setUploadingCount(files.length);
    try {
      const documentIds: string[] = [];
      for (const file of files) {
        const result = await uploadFile(file);
        if (result?.id) documentIds.push(result.id);
      }
      if (documentIds.length > 0) {
        try {
          await dispatchAttachments({
            variables: { projectId, documentIds },
          });
          displayNotification({
            type: "success",
            text: `${documentIds.length} file(s) attached to project`,
          });
        } catch (err) {
          displayNotification({
            type: "error",
            text: err instanceof Error ? err.message : "Failed to attach files",
          });
        }
      }
    } finally {
      setUploadingCount(0);
    }
  };

  const attachments: AttachmentListItem[] = map(
    data?.getProject?.attachments ?? [],
    (att) => ({
      id: att.id,
      filename: att.filename,
      byteSize: att.byteSize,
      previewUrl: att.previewUrl,
      createdAt: att.createdAt ?? null,
      associationType: att.associationType ?? null,
    })
  );
  const isUploading = uploadingCount > 0;

  const onAssociationTypeChange = (documentId: string, associationType: AssociationTypeEnum) => {
    if (!projectId) return;
    setDocumentAssociationType({
      variables: {
        id: documentId,
        attachableType: "Project",
        attachableId: projectId,
        associationType,
      },
      onCompleted: () => {
        displayNotification({
          type: "success",
          text: associationType === AssociationTypeEnum.Soft ? "Set to soft link" : "Set to hard link",
        });
      },
      onError: (err) => {
        displayNotification({
          type: "error",
          text: err.message || "Failed to update association type",
        });
      },
    });
  };

  if (!projectId) {
    return (
      <Box
        sx={{
          bgcolor: grey[100],
          borderRadius: 2,
          p: 2,
          height: "calc(100vh - 165px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Select a project to view files.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: grey[100],
        borderRadius: 2,
        p: 2,
        height: "calc(100vh - 165px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      data-testid="project-files-section"
    >
      <AttachmentsList
        attachments={attachments}
        loading={loading}
        emptyMessage="No files yet. Upload with the button above or drag and drop here."
        onUploadFiles={onUploadFiles}
        isUploading={isUploading}
        onAssociationTypeChange={onAssociationTypeChange}
      />
    </Box>
  );
};

export default ProjectFilesDrawer;
